"use client";

import { Auction, auctionFilterProps, AuctionItem } from "@/types/auction";
import ReactHtmlParser from "html-react-parser";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuctionFilter } from "@/hooks/useAuctionFilter";
import { AUCTION_ITEMS_PER_PAGE } from "@/lib/auctions";
import qs from "query-string";
import { useSearchParams } from "next/navigation";
import AuctionBottomNavbar from "@/components/Auctions/AuctionBottomNavbar";
import AuctionListItems from "@/components/Auctions/AuctionItem/AuctionListItems";
import AuctionCardSlider from "@/components/Auctions/Auction/Card/AuctionCardSlider";
import Link from "next/link";
import { CalendarSvg, formatEndDate, MarkerSvg } from "@/lib/temp";
import useServerTime from "@/hooks/useServerTime";
import HammerSvg from "@/icons/hammer.svg";
import useAuctionItemData from "@/hooks/useAuctionItemData";
import useScrollRestoration from "@/hooks/useScrollRestoration";
import useAuctionItemFiltersRestoration from "@/hooks/useAuctionItemFiltersRestoration";
import { getAuctions } from "@/actions/auctions";
import { SocketProvider } from "@/components/WebSocket/SocketProvider";
import ActiveAuctionTimeSync from "@/components/ActiveAuctionTimeSync";

interface AuctionProps {
  auction: Auction;
  filteringSettings: any;
}

const AuctionClient: React.FC<AuctionProps> = ({
  auction,
  filteringSettings: filteringSettingsServer,
}) => {
  const auctionScrollRef = useRef<HTMLDivElement>(null);

  const [isHydrated, setIsHydrated] = useState(false);
  const [currentQuery, setCurrentQuery] = useState<any>(() => {
    // Initialize with URL params on client side only
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      return qs.parse(params.toString());
    }
    return {};
  });

  const [updatedFirstAvailablePage, setUpdatedFirstAvailablePage] =
    useState<any>(false);

  const [allAuctions, setAllAuctions] = useState<any>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    const fetchAuctions = async () => {
      const { auctions: getAllAuctions } = await getAuctions({
        partStatus: "running",
        limit: 0,
      });
      setAllAuctions(getAllAuctions);
    };

    fetchAuctions();
  }, [isHydrated]);

  const getDefaultSelection = (type: any) =>
    filteringSettingsServer?.options
      ?.filter((setting: any) => setting.matchKey === "itemEndTime")
      ?.flatMap((setting: any) => setting.options || [])
      ?.find((option: any) => option.value === type);

  const [filteredAuctionItems, setFilteredAuctionItems] = useState<
    AuctionItem[]
  >([]);
  const [auctionItemBids, setAuctionItemBids] = useState<AuctionItem[]>([]);
  const [visibleItemsData, setVisibleItemsData] = useState<any>(false);
  const serverTime = useServerTime();
  const auctionPartId = Number(auction.partId);
  const auctionIsNotActive = auction?.partStatus !== "running";

  const maxPossiblePages = Math.ceil(
    filteringSettingsServer?.totalItems / AUCTION_ITEMS_PER_PAGE
  );

  // useSearchParams is reactive — updates correctly on Link nav and browser back
  const urlSearchParams = useSearchParams();
  const pageQuery = urlSearchParams?.get("sida")
    ? Math.min(Number(urlSearchParams.get("sida")) || 1, maxPossiblePages)
    : false;

  const auctionFilterProps = useAuctionFilter({
    auctionPartId: auctionPartId,
    filtering: {
      options: filteringSettingsServer?.options,
      selections: [
        getDefaultSelection(auctionIsNotActive ? "ended" : "active"),
      ],
    },
    pagination: {
      startItemSortNo: filteringSettingsServer?.startItemSortNo,
      lastClosedSortNo: filteringSettingsServer?.lastClosedSortNo,
      page: pageQuery
        ? pageQuery
        : auctionIsNotActive
          ? 1
          : filteringSettingsServer.firstAvailablePage || 1,
      allAvailablePages: filteringSettingsServer?.allAvailablePages || [],
    },
  });

  const { options, setOptions, buildFilterParams } = auctionFilterProps;

  const paginationArgs = options?.pagination as any;
  const filterSelections = options.filtering?.selections;

  const currentPageMemo = useMemo(() => {
    return paginationArgs?.page;
  }, [paginationArgs?.page]);

  // When URL sida param changes (back button, "Del av" link), sync into filter state
  useEffect(() => {
    if (pageQuery && pageQuery !== paginationArgs?.page) {
      setOptions((prev: any) => ({
        ...prev,
        pagination: { ...prev.pagination, page: pageQuery },
      }));
    }
  }, [pageQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const auctionItemSearchValue = options?.searchItemNo || "";

  const setSearchValue = (value: any) => {
    setOptions((prevOptions: any) => ({
      ...prevOptions,
      searchItemNo: value,
    }));
  };

  // Restore filters when navigating back
  useAuctionItemFiltersRestoration(setOptions, options);

  // Set hasPaginated to true when pagination changes
  useEffect(() => {
    if (!paginationArgs?.hasPaginated && currentPageMemo) {
      setOptions((prevOptions: any) => ({
        ...prevOptions,
        pagination: {
          ...prevOptions.pagination,
          hasPaginated: true,
        },
      }));
    }
  }, [currentPageMemo, paginationArgs?.hasPaginated, setOptions]);

  // Keep track of previous page to avoid unnecessary history updates
  const prevUrlPageRef = useRef(currentPageMemo);

  // Update query params when pagination changes
  useEffect(() => {
    // Only update URL if the page has changed
    if (prevUrlPageRef.current !== currentPageMemo && currentPageMemo) {
      const updatedQuery: any = {
        ...currentQuery,
        sida: currentPageMemo,
      };

      const url = qs.stringifyUrl(
        {
          url: window.location.pathname,
          query: updatedQuery,
        },
        { skipNull: true }
      );

      window.history.replaceState(null, "", url);
      prevUrlPageRef.current = currentPageMemo;
    }
  }, [currentPageMemo, currentQuery]);

  const hasPaginatedRef = useRef(false);

  // Scroll to top of the auction list when pagination is triggered
  useEffect(() => {
    if (paginationArgs.hasPaginated && auctionScrollRef.current) {
      if (hasPaginatedRef.current) return; // Prevent looping
      hasPaginatedRef.current = true; // Mark as handled

      auctionScrollRef.current?.scrollIntoView({ behavior: "smooth" });

      window.scrollTo(0, 0);

      setOptions((prevArgs: auctionFilterProps) => ({
        ...prevArgs,
        pagination: {
          ...prevArgs.pagination,
          hasPaginated: false,
        },
      }));
    }
  }, [paginationArgs.hasPaginated]);

  // Use media array if available (new format with mixed sorting)
  // Otherwise fall back to separate images array (old format)
  const hasMedia = auction.media && auction.media.length > 0;

  const cardImages = !hasMedia && auction.images
    ? Object.values(auction.images).map((image: any) => {
        return image;
      })
    : [];

  const {
    error: auctionItemsError,
    hasFetchedFull,
    isLoading,
  } = useAuctionItemData(
    auctionPartId,
    filterSelections,
    serverTime,
    paginationArgs,
    auctionItemSearchValue,
    updatedFirstAvailablePage,
    filteringSettingsServer,
    pageQuery,
    currentPageMemo,
    auctionItemBids,
    auction,
    buildFilterParams,
    setOptions,
    setVisibleItemsData,
    setFilteredAuctionItems,
    setAuctionItemBids,
    setUpdatedFirstAvailablePage
  );

  // Restore scroll position when navigating back
  useScrollRestoration(hasFetchedFull && !isLoading, auctionPartId);

  // Save the ?page query to a session storage
  useEffect(() => {
    if (typeof window !== "undefined") {
      sessionStorage.setItem(
        `auctionPage-${auctionPartId}`,
        String(currentPageMemo)
      );
    }
  }, [currentPageMemo]);

  return (
    <>
      <ActiveAuctionTimeSync isActive={!auctionIsNotActive} />
      <div className="content-wrapper">
        <div className="flex flex-col my-0 lg:mb-6 mb-4 md:max-w-[80%] p-6 bg-white shadow-lg shadow-gray-200 rounded-lg md:!shadow-none md:!bg-transparent md:p-0">
          <div className="md:grid grid-cols-12 gap-6">
            {(hasMedia || cardImages?.length > 0) && (
              <div className="hidden md:block md:col-span-4 max-h-[300px]">
                <AuctionCardSlider
                  media={hasMedia ? auction.media : undefined}
                  images={!hasMedia ? cardImages : undefined}
                  imageClassName="!rounded-lg !object-cover"
                  includeVideos={true}
                  isArchived={auctionIsNotActive}
                />
              </div>
            )}
            <div className="col-span-12 md:col-span-8 flex flex-col rounded-lg md:p-0 md:bg-transparent">
              <div className="flex flex-row items-start justify-start flex-wrap">
                {auctionIsNotActive && (
                  <span className="font-semibold px-2 py-1 md:px-3 md:py-1 bg-light-gray-ultra text-xss md:text-base border mr-4 rounded-lg border border-aliceblue mb-2">
                    ID: {auction.auctionId}
                  </span>
                )}
                <h1 className="font-bold inline-block text-lg md:text-5xl md:text-xxl leading-[1.2] text-modal-blue">
                  {auction.auctionTitle || "Samlingsauktion"}
                </h1>
              </div>
              {auction.partTitle && (
                <div className="flex flex-row items-start justify-start mt-0">
                  <h3 className="relative font-semibold text-dim-gray inline-block text-base text-xs md:text-lg my-4">
                    {ReactHtmlParser(auction.partTitle)}
                  </h3>
                </div>
              )}
              {auction?.partDescription && (
                <div
                  className="self-stretch flex flex-col items-start justify-start mt-4"
                  ref={auctionScrollRef}
                >
                  <div className="self-stretch flex flex-row items-start justify-start">
                    <div className="flex-1 relative leading-[150%] allow-html text-base text-lg">
                      {ReactHtmlParser(auction.partDescription || "")}
                    </div>
                  </div>
                </div>
              )}{" "}
              <div className="w-fit mt-auto ">
                <div className="flex md:mt-8 flex-row md:flex-col gap-4 bg-gray-50 p-2 md:bg-transparent md:p-0">
                  <div className="flex flex-col text-xs md:text-base">
                    <Link
                      href={`https://www.google.com/maps/search/?api=1&query=${auction.partLocation}`}
                      target="_blank"
                      className="!no-underline hover:text-blue "
                    >
                      <div className="flex items-center">
                        <MarkerSvg />
                        <span className="ml-2">{auction.partLocation}</span>
                      </div>
                    </Link>
                  </div>

                  {auction.partAuctionStart &&
                    auction.partAuctionStart !== "0000-00-00 00:00:00" && (
                      <div className="flex items-center">
                        <CalendarSvg />
                        <span className="ml-2 text-xs md:text-base">
                          {formatEndDate(auction.partAuctionStart)}
                        </span>
                      </div>
                    )}
                </div>
              </div>
            </div>
          </div>
        </div>
        {!filteringSettingsServer?.totalItems ||
        filteringSettingsServer?.totalItems <= 0 ? (
          <div className="p-6 bg-white shadow-lg shadow-gray-200 rounded-lg flex flex-col space-y-8 py-12 mt-12">
            <img
              src={HammerSvg.src}
              alt="Hammer"
              className="w-28 h-28 mx-auto"
            />
            <h2 className="text-lg md:text-2xl font-normal text-center text-paragraph">
              Denna auktion verkar inte ha några rop, titta gärna tillbaka lite
              senare.
            </h2>
          </div>
        ) : (
          <AuctionListItems
            auctionItemBids={auctionItemBids}
            auctionItems={filteredAuctionItems}
            filterProps={auctionFilterProps}
            isLoading={visibleItemsData === false}
            isFetching={!hasFetchedFull}
            error={auctionItemsError}
            setItemNoSearchValue={setSearchValue}
            itemNoSearchValue={auctionItemSearchValue}
            visibleItemsData={visibleItemsData}
            showResetFilterOnNotFound
          />
        )}
        <AuctionBottomNavbar
          allAuctions={allAuctions || []}
          currentPartId={auction.partId}
          isLastPage={maxPossiblePages === currentPageMemo}
        />
      </div>
    </>
  );
};

export default AuctionClient;
